import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTranslation } from 'react-i18next';
import { useWindowResize } from '@wbk/hooks';
import { useAnalytics } from '@wbk/analytics';
import { Link, useRouter } from '@wbk/config';
import { parseSectionItem } from '@wbk/contentful';
import { WebSection } from '@wbk/contentful/api';
import { Button } from '@wbk/ui';
import { parseSectionUtm } from '@wbk/utils';
import { formatCustomDate } from '@/utils';
import { getEventRedirectUrl } from '@/utils/redirect';
import EventCard from '@/components/events/EventCard';
import ScrollTitle from '@/components/ui/ScrollTitle';

export default function ExperiencesSection({
  section,
  initialLimit,
  titleClassName,
}: {
  section: WebSection;
  initialLimit?: number;
  titleClassName?: string;
}) {
  const { locale } = useRouter();
  const { t } = useTranslation('common');
  const { trackEvent } = useAnalytics();

  const items = section.contentCollection.items;

  const { width } = useWindowResize();

  const [localItems, setLocalItems] = useState(items);

  useEffect(() => {
    if (width < 640) {
      setLocalItems(items.slice(0, initialLimit || 4));
      return;
    }

    setLocalItems(items?.slice(0, initialLimit || 8));

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to re-run when items or width changes
  }, [items, width]);

  function handleLoadMore() {
    const currentLength = localItems.length;
    const moreItems = items.slice(currentLength, currentLength + 4);
    setLocalItems([...localItems, ...moreItems]);
  }

  return (
    <div className='container-padding container'>
      <section className='container px-3 xl:px-0'>
        <div className='space-y-6 lg:block lg:space-y-12'>
          <div className='flex items-center justify-between'>
            <ScrollTitle title={section.title} className={twMerge('max-w-md', titleClassName)} />
          </div>

          <div className='grid w-full grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4'>
            {localItems.map((item, index) => {
              const parsedItem = parseSectionItem({ item, locale });
              if (!parsedItem) return null;
              const parsed = parsedItem;

              const utm = parseSectionUtm({
                utm: section.utm,
                idx: index,
                id: parsed.id,
                root: !parsed.href?.includes('?'),
              });

              const redirectInfo = getEventRedirectUrl(
                item,
                'slug' in item ? item.slug : '',
                locale,
                parsed,
                utm
              );

              // @ts-expect-error -- schedule is available but not typed
              const openDate = parsed?.schedule?.openDateTime;

              return (
                <Button
                  as={Link}
                  href={redirectInfo.href}
                  theme='white'
                  shape='text'
                  className='first:cols-span-2 w-full p-0 hover:bg-transparent'
                  key={index}
                  target={redirectInfo.isExternal ? '_blank' : undefined}
                  onClick={() => {
                    trackEvent({
                      event: 'clicked_on_section_item',
                      data: {
                        type: item.__typename,
                        slug: 'slug' in item ? item.slug : '',
                        section: section.id,
                      },
                    });
                  }}
                  rel='noreferrer'
                >
                  <EventCard
                    key={index}
                    eventType={parsedItem?.eventType}
                    venueName={parsed.title}
                    eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                    priceFrom={parsed.startingPrice}
                    currency={parsed.currencyCode}
                    image={parsed.image11 || parsed?.image31}
                    // @ts-expect-error -- zone object exists by not typed
                    zoneLogo={parsed?.zone?.zoneLogo}
                    className='w-full'
                    buttonText={parsed?.label || undefined}
                  />
                </Button>
              );
            })}
          </div>

          {localItems.length < items.length && (
            <div className='flex w-full items-center justify-center'>
              <Button
                onClick={handleLoadMore}
                className='rounded-full border border-white bg-transparent px-4 transition-all duration-200 hover:bg-white/10 md:px-8'
              >
                {t('loadMore')}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
